import React, { useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { CONTACT_INFO, IMAGES } from "../mock";
import { useReveal } from "../hooks/useReveal";
import { Phone, Mail, MapPin, Send, CheckCircle2, Star } from "lucide-react";

const Contact = () => {
  const { t } = useLanguage();
  const r = useReveal();
  const [form, setForm] = useState({ name: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.subject) return;

    const body = `${t.contact.name}: ${form.name}\n\n${form.message}`;
    const mailto = `mailto:${CONTACT_INFO.email}?subject=${encodeURIComponent(form.subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
    setStatus("success");
  };

  const handleWhatsApp = (e) => {
    if (!e.currentTarget.form.reportValidity()) return;

    const message = `${t.contact.name}: ${form.name}\n${t.contact.subject}: ${form.subject}\n\n${form.message}`;
    const whatsappUrl = `${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  const inputCls =
    "w-full bg-brand-cream border border-brand-sand rounded-xl px-4 py-3.5 text-brand-ink placeholder-brand-ink/40 focus:outline-none focus:border-brand-plum focus:ring-2 focus:ring-brand-plum/15 transition-all";

  return (
    <section id="contact" className="py-16 sm:py-24 md:py-32 bg-gradient-to-b from-brand-cream to-brand-sand">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-10 sm:mb-14">
          <p className="text-xs tracking-[0.4em] uppercase text-brand-gold mb-4">
            {t.contact.kicker}
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-brand-plumDark mb-4">
            {t.contact.title}
          </h2>
          <p className="text-brand-ink/70 max-w-2xl mx-auto leading-relaxed">
            {t.contact.subtitle}
          </p>
        </div>

        <div ref={r.ref} className={`${r.className} grid lg:grid-cols-5 gap-6 sm:gap-8`}>
          {/* Info panel */}
          <div className="lg:col-span-2 relative rounded-[2rem] overflow-hidden min-h-[320px] sm:min-h-[420px] flex">
            <img
              src={IMAGES.bowls}
              alt="Contatto"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-plumDark/90 via-brand-plumDark/60 to-brand-plumDark/30" />
            <div className="relative mt-auto p-8 text-brand-cream space-y-5 w-full">
              <a href={CONTACT_INFO.phoneHref} className="flex items-center gap-4 group">
                <span className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center group-hover:bg-brand-gold transition-colors">
                  <Phone size={18} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-brand-lavender">{t.contact.phoneLabel}</p>
                  <p className="text-brand-cream">{CONTACT_INFO.phone}</p>
                </div>
              </a>
              <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-4 group">
                <span className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center group-hover:bg-brand-gold transition-colors">
                  <Mail size={18} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-brand-lavender">{t.contact.emailLabel}</p>
                  <p className="text-brand-cream break-all">{CONTACT_INFO.email}</p>
                </div>
              </a>
              <a href={CONTACT_INFO.mapUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
                <span className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center group-hover:bg-brand-gold transition-colors">
                  <MapPin size={18} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-brand-lavender">{t.contact.locationLabel}</p>
                  <p className="text-brand-cream">{CONTACT_INFO.location}</p>
                  <p className="text-brand-cream/70 text-sm leading-snug">{CONTACT_INFO.addressFull}</p>
                </div>
              </a>
              <a
                href={CONTACT_INFO.reviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white/15 hover:bg-brand-gold hover:text-brand-plumDark text-brand-cream text-sm px-5 py-2.5 rounded-full transition-colors"
                data-testid="contact-review-button"
              >
                <Star size={16} className="fill-current" />
                {t.contact.reviewAction}
              </a>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="lg:col-span-3 bg-white rounded-[2rem] p-8 md:p-10 shadow-[0_20px_60px_rgba(76,62,99,0.08)] border border-brand-sand"
            data-testid="contact-form"
          >
            <div className="mb-5">
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder={t.contact.name}
                required
                className={inputCls}
                data-testid="contact-name-input"
              />
            </div>
            <input
              name="subject"
              value={form.subject}
              onChange={handleChange}
              placeholder={t.contact.subject}
              required
              className={`${inputCls} mb-5`}
              data-testid="contact-subject-input"
            />
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder={t.contact.message}
              rows={5}
              className={`${inputCls} mb-6 resize-none`}
              data-testid="contact-message-input"
            />

            {status === "success" && (
              <div className="flex items-center gap-2 text-brand-sage mb-5" data-testid="contact-success-message" role="status">
                <CheckCircle2 size={18} /> <span className="text-sm">{t.contact.mailtoSuccess}</span>
              </div>
            )}

            <div className="grid gap-3 border-t border-brand-sand pt-6 sm:grid-cols-2">
              <button
                type="button"
                onClick={handleWhatsApp}
                className="group inline-flex min-h-[58px] w-full items-center justify-center gap-2.5 rounded-full bg-[#25D366] px-7 py-4 text-white shadow-[0_10px_22px_rgba(37,211,102,0.25)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[#128C7E] hover:shadow-[0_14px_28px_rgba(18,140,126,0.3)]"
                data-testid="contact-whatsapp-button"
              >
                <svg viewBox="0 0 32 32" className="h-5 w-5 fill-current" aria-hidden="true">
                  <path d="M27.4 4.6A15.4 15.4 0 0 0 3.2 23.1L1 31l8.1-2.1A15.4 15.4 0 1 0 27.4 4.6ZM16.1 28a12.4 12.4 0 0 1-6.3-1.7l-.5-.3-4.8 1.2 1.3-4.7-.3-.5a12.4 12.4 0 1 1 10.6 6Zm6.8-9.3c-.4-.2-2.5-1.2-2.9-1.3-.4-.2-.7-.2-1 .2-.3.4-1.1 1.3-1.3 1.6-.2.3-.5.3-.9.1a10.2 10.2 0 0 1-3-1.9 11.4 11.4 0 0 1-2.1-2.7c-.2-.4 0-.6.1-.8l.7-.8c.2-.2.3-.4.4-.7.1-.3 0-.5 0-.7-.1-.2-1-2.3-1.3-3.2-.4-.9-.7-.7-1-.7h-.8c-.3 0-.7.1-1.1.5-.4.4-1.4 1.4-1.4 3.5 0 2 1.5 4 1.7 4.3.2.3 2.9 4.5 7.1 6.3 1 .4 1.8.7 2.4.9 1 .3 1.9.2 2.6.1.8-.1 2.5-1 2.9-2 .4-1 .4-1.8.3-2-.1-.2-.3-.3-.7-.5Z" />
                </svg>
                WhatsApp
              </button>
              <button
                type="submit"
                className="group inline-flex min-h-[58px] w-full items-center justify-center gap-2 rounded-full border border-brand-plum/35 bg-white px-7 py-4 text-brand-plum transition-[background-color,color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-brand-plum hover:text-brand-cream hover:shadow-lg"
                data-testid="contact-submit-button"
              >
                {t.contact.emailAction}
                <Send size={17} className="transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>
          </form>
        </div>

        <div className="mt-8 rounded-[2rem] overflow-hidden border border-brand-sand shadow-[0_20px_60px_rgba(76,62,99,0.08)]">
          <iframe
            title={t.contact.mapTitle}
            src={CONTACT_INFO.mapEmbed}
            width="100%"
            height="360"
            style={{ border: 0, display: "block" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
            data-testid="contact-map"
          />
        </div>
      </div>
    </section>
  );
};

export default Contact;
